import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, LogIn, Rocket, Shield } from 'lucide-react';
import { navigation, protectionLayersDropdown } from '../../data/mock';
import { Button } from '../ui/button';

// Auth helper to check login state
const isUserLoggedIn = () => {
  return !!localStorage.getItem('unified_token');
};

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [protectionLayersOpen, setProtectionLayersOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);

  // Module context detection
  const isCouncilGuard = location.pathname.startsWith('/councilguard');
  const moduleContext = isCouncilGuard ? 'CouncilGuard' : null;

  // Check login state on mount and route changes
  useEffect(() => {
    setIsLoggedIn(isUserLoggedIn());
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setProtectionLayersOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (href) => location.pathname === href;

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled 
        ? 'bg-[#0a1929]/95 backdrop-blur-md border-b border-[#48a8ab]/10 shadow-lg shadow-black/10' 
        : 'bg-transparent'
    }`}>
      <nav className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="header-logo-link">
            <span className="text-2xl font-bold text-white tracking-tight">
              K<span className="text-[#48a8ab]">AI</span>YA
            </span>
            <span className="ml-2 text-sm text-gray-400 hidden sm:inline">SOLUTIONS</span>
            {moduleContext && (
              <span className="ml-3 pl-3 border-l border-gray-600 text-sm font-medium text-[#48a8ab] hidden sm:inline" data-testid="header-module-context">
                {moduleContext}
              </span>
            )}
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {/* Nav Items - render dropdown or regular link based on isDropdown flag */}
            {navigation.map((item) => {
              if (item.isDropdown) {
                return (
                  <div key={item.name} className="relative" ref={dropdownRef}>
                    <button
                      onClick={() => setProtectionLayersOpen(!protectionLayersOpen)}
                      className={`flex items-center gap-1 text-sm font-medium transition-colors duration-200 ${
                        protectionLayersOpen ? 'text-[#48a8ab]' : 'text-gray-300 hover:text-[#48a8ab]'
                      }`}
                      data-testid="protection-layers-dropdown-btn"
                    >
                      {item.name}
                      <ChevronDown className={`w-4 h-4 transition-transform ${protectionLayersOpen ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {protectionLayersOpen && (
                      <div className="absolute top-full left-0 mt-2 w-72 bg-[#0d2137] border border-[#1e3a5f] rounded-xl shadow-xl shadow-black/20 py-2 z-50">
                        {protectionLayersDropdown.map((dropdownItem) => (
                          <Link
                            key={dropdownItem.href}
                            to={dropdownItem.href}
                            className="flex flex-col px-4 py-3 hover:bg-[#143552] transition-colors"
                            onClick={() => setProtectionLayersOpen(false)}
                          >
                            <span className="text-white font-medium">{dropdownItem.name}</span>
                            <span className="text-gray-500 text-xs">{dropdownItem.description}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`text-sm font-medium transition-colors duration-200 ${
                    isActive(item.href)
                      ? 'text-[#48a8ab]'
                      : 'text-gray-300 hover:text-[#48a8ab]'
                  }`}
                >
                  {item.name}
                </Link>
              );
            })}
            
            {isLoggedIn ? (
              <Link to="/app">
                <Button className="bg-[#48a8ab] hover:bg-[#3d9194] text-white px-6 shadow-lg shadow-[#48a8ab]/20" data-testid="open-app-btn">
                  <Rocket size={16} className="mr-2" />
                  Open App
                </Button>
              </Link>
            ) : (
              <>
                <Link 
                  to="/login"
                  className="flex items-center gap-1.5 text-sm font-medium text-gray-300 hover:text-[#48a8ab] transition-colors duration-200"
                  data-testid="login-btn"
                >
                  <LogIn size={16} />
                  Login
                </Link>
                <Link to="/risk-assessment">
                  <Button className="bg-[#48a8ab] hover:bg-[#3d9194] text-white px-6 shadow-lg shadow-[#48a8ab]/20" data-testid="request-assessment-btn">
                    <Shield size={16} className="mr-2" />
                    Request Risk Assessment
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-gray-300"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-[#48a8ab]/20 bg-[#0a1929]/95 backdrop-blur-md">
            <div className="flex flex-col gap-2">
              {/* Protection Layers Section */}
              <div className="px-2 py-2">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 px-2">Protection Layers</p>
                {protectionLayersDropdown.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className="block px-2 py-2 text-sm text-gray-300 hover:text-[#48a8ab] transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
              
              <div className="border-t border-[#1e3a5f] my-2" />
              
              {/* Other Nav Items */}
              {navigation.filter(item => !item.isDropdown).map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`text-sm font-medium py-2 px-2 transition-colors duration-200 ${
                    isActive(item.href)
                      ? 'text-[#48a8ab]'
                      : 'text-gray-300 hover:text-[#48a8ab]'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              
              <div className="border-t border-[#1e3a5f] my-2" />
              
              {isLoggedIn ? (
                <div className="px-2 pt-2">
                  <Link to="/app" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full bg-[#48a8ab] hover:bg-[#3d9194] text-white" data-testid="mobile-open-app-btn">
                      <Rocket size={16} className="mr-2" />
                      Open App
                    </Button>
                  </Link>
                </div>
              ) : (
                <>
                  <Link 
                    to="/login"
                    className="flex items-center gap-2 text-sm font-medium py-2 px-2 text-gray-300 hover:text-[#48a8ab] transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-login-btn"
                  >
                    <LogIn size={16} />
                    Login
                  </Link>
                  
                  <div className="px-2 pt-2">
                    <Link to="/risk-assessment" onClick={() => setMobileMenuOpen(false)}>
                      <Button className="w-full bg-[#48a8ab] hover:bg-[#3d9194] text-white" data-testid="mobile-request-assessment-btn">
                        <Shield size={16} className="mr-2" />
                        Request Risk Assessment
                      </Button>
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
